import React, { useCallback, useEffect } from "react";

import { useRouter } from "next/navigation";

import { Sparkles } from "lucide-react";
import { toast } from "sonner";

import { RelatedProductCard } from "@/components/product/RelatedProductCard";
import { CarouselArrow } from "@/components/ui/CarouselArrow";
import { useHorizontalScroll } from "@/hooks/useHorizontalScroll";
import { cacheProductForPDP } from "@/lib/product-store";
import {
  PRODUCT_NAVIGATION_UNAVAILABLE_MESSAGE,
  buildCanonicalProductPathFromRecord,
  warnMissingCanonicalProductPath,
} from "@/lib/product-url";
import { type Product, getProductAnyId } from "@/lib/product-utils";

function getRelatedProductRailItemKey(relatedProduct: Product, index: number): string {
  const relatedProductId = getProductAnyId(relatedProduct);
  return relatedProductId !== "unknown" ? relatedProductId : `idx-${index}`;
}

interface RelatedProductsRailProps {
  relatedProducts: Product[];
  loadingRelated: boolean;
  currentProductId: string | null;
}

export function RelatedProductsRail({
  relatedProducts,
  loadingRelated,
  currentProductId,
}: RelatedProductsRailProps) {
  const router = useRouter();

  const navigateToRelatedProduct = useCallback(
    (relatedProduct: Product) => {
      const relatedProductId = getProductAnyId(relatedProduct);
      if (relatedProductId !== "unknown") {
        try {
          cacheProductForPDP(relatedProductId, relatedProduct);
        } catch {
          // Ignore storage failures; navigation still succeeds and destination can fetch by ID.
        }
      }
      const productPath = buildCanonicalProductPathFromRecord(
        relatedProduct as unknown as Record<string, unknown>,
        relatedProductId
      );
      if (!productPath) {
        warnMissingCanonicalProductPath("RelatedProductsRail", relatedProductId);
        toast.error(PRODUCT_NAVIGATION_UNAVAILABLE_MESSAGE);
        return;
      }
      router.push(productPath);
    },
    [router]
  );

  const {
    scrollRef: relatedDesktopCarouselRef,
    canScrollLeft: canScrollLeftDesktop,
    canScrollRight: canScrollRightDesktop,
    scrollLeft: scrollCarouselLeftDesktop,
    scrollRight: scrollCarouselRightDesktop,
    scrollToStart: scrollRelatedDesktopToStart,
  } = useHorizontalScroll({
    threshold: 1,
    scrollAmount: "viewport",
    viewportMultiplier: 0.75,
    deps: [relatedProducts.length],
  });

  const {
    scrollRef: relatedMobileCarouselRef,
    canScrollLeft: canScrollLeftMobile,
    canScrollRight: canScrollRightMobile,
    scrollLeft: scrollCarouselLeftMobile,
    scrollRight: scrollCarouselRightMobile,
    scrollToStart: scrollRelatedMobileToStart,
  } = useHorizontalScroll({
    threshold: 1,
    scrollAmount: 200,
    deps: [relatedProducts.length],
  });

  useEffect(() => {
    scrollRelatedDesktopToStart();
    scrollRelatedMobileToStart();
  }, [currentProductId, scrollRelatedDesktopToStart, scrollRelatedMobileToStart]);

  const renderRelatedRailItems = (
    itemClassName: string,
    renderCard: (relatedProduct: Product) => React.ReactNode
  ) => {
    return relatedProducts.map((relatedProduct, index) => (
      <div key={getRelatedProductRailItemKey(relatedProduct, index)} className={itemClassName}>
        {renderCard(relatedProduct)}
      </div>
    ));
  };

  if (!loadingRelated && relatedProducts.length === 0) {
    return null;
  }

  return (
    <div className="mt-8 border-t border-gray-100 lg:mt-0">
      {loadingRelated ? (
        <div className="flex justify-center py-16">
          <div className="text-center">
            <div className="mb-3 inline-block h-8 w-8 animate-spin rounded-full border-4 border-gray-400 border-t-transparent" />
            <p className="text-sm text-gray-500">Finding similar items...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Desktop — 4 across, horizontally scrollable, hidden scrollbar */}
          <div className="relative hidden lg:block">
            <div className="px-8 pb-4 pt-10">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-medium text-gray-900">More Like This</h2>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Sparkles className="h-4 w-4" />
                  <span>Powered by AI similarity search</span>
                </div>
              </div>
            </div>
            {canScrollLeftDesktop && (
              <CarouselArrow
                direction="prev"
                onClick={scrollCarouselLeftDesktop}
                size="sm"
                className="absolute left-2 top-[55%] z-10 -translate-y-1/2"
              />
            )}
            {canScrollRightDesktop && (
              <CarouselArrow
                direction="next"
                onClick={scrollCarouselRightDesktop}
                size="sm"
                className="absolute right-2 top-[55%] z-10 -translate-y-1/2"
              />
            )}
            <div
              ref={relatedDesktopCarouselRef}
              className="flex overflow-x-auto pb-8 [&::-webkit-scrollbar]:hidden"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {renderRelatedRailItems("w-1/4 flex-shrink-0", (relatedProduct) => (
                <RelatedProductCard
                  product={relatedProduct}
                  onClick={() => navigateToRelatedProduct(relatedProduct)}
                  variant="editorial"
                />
              ))}
            </div>
          </div>

          {/* Mobile — Carousel */}
          <div className="relative px-6 py-10 lg:hidden">
            <h2 className="mb-6 text-lg font-medium text-gray-900">More Like This</h2>
            {canScrollLeftMobile && (
              <CarouselArrow
                direction="prev"
                onClick={scrollCarouselLeftMobile}
                size="sm"
                className="absolute -left-2 top-1/3 z-10 -translate-y-1/2"
              />
            )}
            {canScrollRightMobile && (
              <CarouselArrow
                direction="next"
                onClick={scrollCarouselRightMobile}
                size="sm"
                className="absolute -right-2 top-1/3 z-10 -translate-y-1/2"
              />
            )}
            <div
              ref={relatedMobileCarouselRef}
              className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4"
              style={{
                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {renderRelatedRailItems("w-40 flex-shrink-0 snap-start", (relatedProduct) => (
                <RelatedProductCard
                  product={relatedProduct}
                  onClick={() => navigateToRelatedProduct(relatedProduct)}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
